import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { usePricing } from "../usePricing";
import { Spinner } from "../components/ui";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const pricing = usePricing();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const sessionId = params.get("session_id") || params.get("subscription_id");

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState("failed");
        return;
      }
      if (attempt > 10) {
        setState("failed");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        console.debug("Payment status not ready yet, retrying:", e);
      }
      setTimeout(() => poll(attempt + 1), 2000);
    },
    [sessionId, refresh]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="card p-8 max-w-md w-full text-center fade-in" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Spinner className="w-8 h-8 mx-auto text-teal mb-4" />
            <h1 className="font-display text-xl text-ink mb-2">Confirming your payment…</h1>
            <p className="text-sm text-muted">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald mb-4" />
            <h1 className="font-display text-2xl text-ink mb-2">You're on Pro</h1>
            <p className="text-sm text-muted mb-6">{pricing.display}/{pricing.period} · Your subscription is active.</p>
            <button className="btn-primary w-full" onClick={() => navigate("/dashboard")} data-testid="payment-continue">
              Go to dashboard
            </button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-rose mb-4" />
            <h1 className="font-display text-xl text-ink mb-2">Payment not confirmed</h1>
            <p className="text-sm text-muted mb-6">We couldn't confirm your payment. You can try again.</p>
            <button className="btn-primary w-full" onClick={() => navigate("/pricing")} data-testid="payment-retry">
              Back to pricing
            </button>
          </>
        )}
      </div>
    </div>
  );
}
